import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { BottomArrow } from "@/Icon/BottomArrow";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import MessageModal from "@/MessageModal";
import { useState, useMemo, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import { format, parse } from "date-fns";
import {
  useUserUsageHistory,
  type UserUsageHistoryItem,
} from "~/services/customer/usageHistory";
import { useLoadingStore } from "../../../stores/loading";
import {
  useUsageHistoryStore,
  type PeriodPreset,
} from "../../../stores/usageHistory";

export const Route = createFileRoute("/customer/layout/user-usage-history")({
  component: UserUsageHistory,
});

const PRESET_MONTHS: readonly PeriodPreset[] = ["1", "3", "6", "12"];
// 30분 이상 방치된 검색 상태는 진입 시 자동 리셋
const STALE_THRESHOLD_MS = 30 * 60 * 1000;

function formatDisplayDate(ymd: string) {
  if (!ymd) return "";
  return dayjs(ymd).format("YYYY.MM.DD");
}

function formatTimeRange(item: UserUsageHistoryItem): string | null {
  // 등하원 시간이 기록되어 있으면 그것 우선
  if (item.checkInTime && item.checkOutTime) {
    return `${dayjs(item.checkInTime).format("HH:mm")} ~ ${dayjs(
      item.checkOutTime
    ).format("HH:mm")}`;
  }
  if (item.checkInTime) {
    return `${dayjs(item.checkInTime).format("HH:mm")} 등원`;
  }
  if (item.checkOutTime) {
    return `${dayjs(item.checkOutTime).format("HH:mm")} 하원`;
  }
  // 등하원 기록이 없으면 예약 시간 fallback
  if (item.startTime && item.endTime) {
    return `${item.startTime.slice(0, 5)} ~ ${item.endTime.slice(0, 5)}`;
  }
  if (item.startTime) return item.startTime.slice(0, 5);
  if (item.endTime) return item.endTime.slice(0, 5);
  return null;
}

function formatReservDate(item: UserUsageHistoryItem): string {
  if (!item.dtReservStart) return "-";
  const start = dayjs(item.dtReservStart).format("YYYY.MM.DD");
  // 호텔만 박수 범위 표시. 수업/데이케어/미용/훈련은 단일 일자.
  if (
    item.cdReservType === "RESERV_TYPE_HOTEL" &&
    item.dtReservEnd &&
    item.dtReservEnd !== item.dtReservStart
  ) {
    return `${start} ~ ${dayjs(item.dtReservEnd).format("MM.DD")}`;
  }
  return start;
}

function UserUsageHistory() {
  const navigate = useNavigate();

  // 컴포넌트 마운트 시점에 오래된 상태면 자동 리셋
  const resetIfStale = useUsageHistoryStore((s) => s.resetIfStale);
  useEffect(() => {
    resetIfStale(STALE_THRESHOLD_MS);
  }, [resetIfStale]);

  const expanded = useUsageHistoryStore((s) => s.expanded);
  const setExpanded = useUsageHistoryStore((s) => s.setExpanded);
  const periodPreset = useUsageHistoryStore((s) => s.periodPreset);
  const setPeriodPreset = useUsageHistoryStore((s) => s.setPeriodPreset);
  const startDate = useUsageHistoryStore((s) => s.startDate);
  const setStartDate = useUsageHistoryStore((s) => s.setStartDate);
  const endDate = useUsageHistoryStore((s) => s.endDate);
  const setEndDate = useUsageHistoryStore((s) => s.setEndDate);
  const searchKeyword = useUsageHistoryStore((s) => s.searchKeyword);
  const setSearchKeyword = useUsageHistoryStore((s) => s.setSearchKeyword);
  const isCustomDateRange = useUsageHistoryStore((s) => s.isCustomDateRange);
  const setIsCustomDateRange = useUsageHistoryStore(
    (s) => s.setIsCustomDateRange
  );
  const appliedParams = useUsageHistoryStore((s) => s.appliedParams);
  const refetchKey = useUsageHistoryStore((s) => s.refetchKey);
  const applySearch = useUsageHistoryStore((s) => s.applySearch);
  const resetInputs = useUsageHistoryStore((s) => s.resetInputs);

  const [isStartDateModalOpen, setIsStartDateModalOpen] = useState(false);
  const [isEndDateModalOpen, setIsEndDateModalOpen] = useState(false);

  const apiParams = useMemo(
    () => ({
      searchKeyword: appliedParams.searchKeyword,
      startDate: appliedParams.startDate,
      endDate: appliedParams.endDate,
    }),
    [
      appliedParams.searchKeyword,
      appliedParams.startDate,
      appliedParams.endDate,
    ]
  );

  const { data, isLoading, isFetching, error } = useUserUsageHistory(
    apiParams,
    { refetchKey }
  );

  const { toggleLoading } = useLoadingStore();
  useEffect(() => {
    toggleLoading(isLoading || isFetching);
  }, [isLoading, isFetching, toggleLoading]);

  const summaryText = useMemo(() => {
    const period = isCustomDateRange ? "직접 선택" : `${periodPreset}개월`;
    const keywordPart = appliedParams.searchKeyword ? "검색어" : "전체";
    return `${period} · ${keywordPart} · 최신순`;
  }, [periodPreset, appliedParams.searchKeyword, isCustomDateRange]);

  const applyPreset = useCallback(
    (preset: PeriodPreset) => {
      setIsCustomDateRange(false);
      setPeriodPreset(preset);
      const today = dayjs();
      const months = Number.parseInt(preset, 10);
      setEndDate(today.format("YYYY-MM-DD"));
      setStartDate(today.subtract(months, "month").format("YYYY-MM-DD"));
    },
    [setIsCustomDateRange, setPeriodPreset, setEndDate, setStartDate]
  );

  const handleStartDateSelect = (day: Date | null) => {
    if (day) {
      setIsCustomDateRange(true);
      const value = format(day, "yyyy-MM-dd");
      setStartDate(value);
      if (endDate && dayjs(value).isAfter(dayjs(endDate))) {
        setEndDate(value);
      }
      setIsStartDateModalOpen(false);
    }
  };

  const handleEndDateSelect = (day: Date | null) => {
    if (day) {
      setIsCustomDateRange(true);
      const value = format(day, "yyyy-MM-dd");
      setEndDate(value);
      if (startDate && dayjs(value).isBefore(dayjs(startDate))) {
        setStartDate(value);
      }
      setIsEndDateModalOpen(false);
    }
  };

  const handleSearch = () => {
    applySearch({
      searchKeyword: searchKeyword.trim() || undefined,
      startDate,
      endDate,
    });
  };

  const handleReset = () => {
    resetInputs();
  };

  const list: UserUsageHistoryItem[] = data?.data ?? [];

  return (
    <>
      <LayoutHeader title="이용 내역" hasBackBtn hasFootPrint={false} />
      <div className="p-3 flex-auto bg-[#F5F5F5]">
        {/* 검색 영역 */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            type="button"
            className="flex justify-between items-center px-4 py-3 w-full text-left bg-white border-b border-gray-100"
            onClick={() => setExpanded(!expanded)}
          >
            <span className="text-sm text-gray-700">{summaryText}</span>
            <span
              className={`text-gray-500 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
            >
              <BottomArrow />
            </span>
          </button>
          <div className="px-4 py-2.5 bg-gray-50">
            <span className="text-sm text-gray-600">
              {formatDisplayDate(appliedParams.startDate)} ~{" "}
              {formatDisplayDate(appliedParams.endDate)}
            </span>
          </div>

          {expanded && (
            <div className="px-4 py-4 space-y-4 border-t border-gray-100 bg-gray-50/50">
              <div>
                <p className="mb-2 text-xs font-medium text-gray-500">
                  조회 기간
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {PRESET_MONTHS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => applyPreset(preset)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                        !isCustomDateRange && periodPreset === preset
                          ? "bg-orange-400 text-white border-orange-400"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-orange-50"
                      }`}
                    >
                      {preset}개월
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => setIsStartDateModalOpen(true)}
                    className="flex-1 min-w-[120px] px-3 py-2 text-sm text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                  >
                    {formatDisplayDate(startDate)}
                  </button>
                  <span className="text-gray-400">~</span>
                  <button
                    type="button"
                    onClick={() => setIsEndDateModalOpen(true)}
                    className="flex-1 min-w-[120px] px-3 py-2 text-sm text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                  >
                    {formatDisplayDate(endDate)}
                  </button>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-gray-500">
                  키워드(선택)
                </p>
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="유치원/사업장 또는 반려동물 이름"
                  className="px-3 py-2 w-full text-sm placeholder-gray-400 bg-white rounded-lg border border-gray-300"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                >
                  <span className="text-base" aria-hidden>
                    ↺
                  </span>{" "}
                  초기화
                </button>
                <button
                  type="button"
                  onClick={handleSearch}
                  className="flex-1 py-2.5 rounded-lg bg-orange-400 text-white text-sm font-medium"
                >
                  조회
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 시작일 선택 달력 */}
        <MessageModal
          isOpen={isStartDateModalOpen}
          onRequestClose={() => setIsStartDateModalOpen(false)}
          className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
          overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          shouldCloseOnOverlayClick={false}
        >
          <CalendarComponent
            disabled={false}
            disablePast={false}
            dayOnClick={handleStartDateSelect}
            selectedDay={
              startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : null
            }
            schedules={{}}
          />
          <div className="flex gap-3 px-4 pt-2">
            <button
              type="button"
              onClick={() => setIsStartDateModalOpen(false)}
              className="flex-1 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            >
              취소
            </button>
            <button
              type="button"
              onClick={() => setIsStartDateModalOpen(false)}
              className="flex-1 py-2 text-sm font-semibold text-white bg-orange-400 rounded-full"
            >
              확인
            </button>
          </div>
        </MessageModal>

        {/* 종료일 선택 달력 */}
        <MessageModal
          isOpen={isEndDateModalOpen}
          onRequestClose={() => setIsEndDateModalOpen(false)}
          className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
          overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          shouldCloseOnOverlayClick={false}
        >
          <CalendarComponent
            disabled={false}
            disablePast={false}
            minDate={
              startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : undefined
            }
            maxDate={new Date()}
            dayOnClick={handleEndDateSelect}
            selectedDay={
              endDate ? parse(endDate, "yyyy-MM-dd", new Date()) : null
            }
            schedules={{}}
          />
          <div className="flex gap-3 px-4 pt-2">
            <button
              type="button"
              onClick={() => setIsEndDateModalOpen(false)}
              className="flex-1 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            >
              취소
            </button>
            <button
              type="button"
              onClick={() => setIsEndDateModalOpen(false)}
              className="flex-1 py-2 text-sm font-semibold text-white bg-orange-400 rounded-full"
            >
              확인
            </button>
          </div>
        </MessageModal>

        {/* 리스트 */}
        <div className="mt-3">
          {isLoading && (
            <div className="py-16 text-sm text-center text-gray-500 bg-white rounded-xl border border-[#E9E9E9]">
              조회 중...
            </div>
          )}
          {!isLoading && error && (
            <div className="py-16 text-sm text-center text-red-500 bg-white rounded-xl border border-[#E9E9E9]">
              이용내역을 불러오지 못했습니다.
            </div>
          )}
          {!isLoading && !error && list.length === 0 && (
            <div className="py-16 text-center bg-white rounded-xl border border-[#E9E9E9]">
              <p className="text-sm text-gray-500">
                이용내역이 없습니다.
              </p>
              <p className="mt-1.5 text-xs text-gray-400">
                선택한 기간에 이용한 내역이 없습니다.
              </p>
            </div>
          )}
          {!isLoading && !error && list.length > 0 && (
            <div className="flex flex-col gap-3">
              {list.map((item) => (
                <UsageHistoryCard
                  key={item.reservationSeq}
                  item={item}
                  onClick={() =>
                    navigate({
                      to: "/customer/layout/user-usage-history-detail",
                      search: { reservationSeq: item.reservationSeq },
                    })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

interface UsageHistoryCardProps {
  item: UserUsageHistoryItem;
  onClick: () => void;
}

const RESERV_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  RESERV_TYPE_CLASS: {
    label: "수업",
    className: "text-orange-500 bg-orange-50",
  },
  RESERV_TYPE_DAYCARE: {
    label: "데이케어",
    className: "text-sky-600 bg-sky-50",
  },
  RESERV_TYPE_HOTEL: {
    label: "호텔링",
    className: "text-purple-600 bg-purple-50",
  },
  RESERV_TYPE_GROOMING: {
    label: "미용",
    className: "text-pink-600 bg-pink-50",
  },
  RESERV_TYPE_TRAINING: {
    label: "훈련",
    className: "text-emerald-600 bg-emerald-50",
  },
};

const UsageHistoryCard = ({ item, onClick }: UsageHistoryCardProps) => {
  const dateText = formatReservDate(item);
  const timeText = formatTimeRange(item);
  const badge = RESERV_TYPE_BADGE[item.cdReservType] ?? {
    label: item.productName ?? "이용",
    className: "text-gray-600 bg-gray-100",
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="p-4 bg-white rounded-xl border border-[#E9E9E9] transition-colors cursor-pointer hover:border-orange-300 active:bg-gray-50"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* 헤더: 펫 + 서비스 뱃지 */}
      <div className="flex gap-3 items-start">
        <div className="overflow-hidden w-12 h-12 bg-gray-100 rounded-full shrink-0">
          {item.fileUrl ? (
            <img
              src={item.fileUrl}
              alt={item.petName}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="flex justify-center items-center w-full h-full text-xs text-gray-400">
              {item.petName?.[0] ?? "N"}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 justify-between items-start">
            <p className="text-base font-bold text-gray-900 truncate">
              {item.petName}
            </p>
            <span
              className={`shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}
            >
              {badge.label}
            </span>
          </div>
          {item.companyName && (
            <p className="mt-0.5 text-sm text-gray-500 truncate">
              {item.companyName}
            </p>
          )}
        </div>
      </div>

      {/* 본문: 일자 + 시간 + 이용권 */}
      <div className="pt-3 mt-3 border-t border-gray-100">
        <div className="flex gap-2 justify-between items-baseline">
          <span className="text-sm font-medium text-gray-900">{dateText}</span>
          {item.durationText && (
            <span className="text-xs font-medium text-orange-500 whitespace-nowrap">
              총 {item.durationText}
            </span>
          )}
        </div>
        {timeText && (
          <p className="mt-1 text-xs text-gray-500">{timeText}</p>
        )}
        {item.ticketName && (
          <div className="inline-flex items-center px-2 py-0.5 mt-2 text-xs text-gray-600 bg-gray-100 rounded-md max-w-full">
            <span className="mr-1 text-gray-400">이용권</span>
            <span className="truncate">{item.ticketName}</span>
          </div>
        )}
      </div>
    </div>
  );
};
